/**
 * Firebase Auth Error Utility
 * Firebase Auth 에러를 사용자 친화적인 메시지로 변환
 */

interface FirebaseError extends Error {
  code?: string;
}

/**
 * Firebase Auth 에러 코드를 한국어 사용자 친화적 메시지로 변환
 * @param error - Firebase 에러 객체
 * @returns 사용자에게 표시할 친화적인 에러 메시지
 */
export const getFirebaseErrorMessage = (error: FirebaseError): string => {
  if (!error.code) {
    return error.message || '알 수 없는 오류가 발생했습니다.';
  }

  switch (error.code) {
    // 이메일/비밀번호 인증 관련
    case 'auth/email-already-in-use':
      return '이미 사용 중인 이메일입니다. 다른 이메일을 사용해주세요.';

    case 'auth/invalid-email':
      return '올바른 이메일 주소를 입력해주세요.';

    case 'auth/weak-password':
      return '비밀번호는 최소 6자 이상이어야 합니다.';

    case 'auth/user-not-found':
      return '존재하지 않는 계정입니다. 이메일을 확인해주세요.';

    case 'auth/wrong-password':
      return '비밀번호가 올바르지 않습니다. 다시 시도해주세요.';

    case 'auth/invalid-credential':
      return '이메일 또는 비밀번호가 올바르지 않습니다.';

    case 'auth/user-disabled':
      return '비활성화된 계정입니다. 고객센터에 문의해주세요.';

    case 'auth/operation-not-allowed':
      return '이 로그인 방식은 현재 사용할 수 없습니다.';

    // 네트워크 관련
    case 'auth/network-request-failed':
      return '네트워크 연결을 확인해주세요.';

    case 'auth/timeout':
      return '요청 시간이 초과되었습니다. 다시 시도해주세요.';

    // 요청 제한
    case 'auth/too-many-requests':
      return '너무 많은 시도가 있었습니다. 잠시 후 다시 시도해주세요.';

    // 토큰 관련
    case 'auth/id-token-expired':
    case 'auth/id-token-revoked':
      return '로그인 세션이 만료되었습니다. 다시 로그인해주세요.';

    // 비밀번호 재설정
    case 'auth/invalid-action-code':
      return '유효하지 않거나 만료된 링크입니다.';

    case 'auth/expired-action-code':
      return '만료된 링크입니다. 비밀번호 재설정을 다시 요청해주세요.';

    // 인증 관련
    case 'auth/requires-recent-login':
      return '보안을 위해 다시 로그인해주세요.';

    case 'auth/invalid-verification-code':
      return '인증 코드가 올바르지 않습니다.';

    case 'auth/invalid-verification-id':
      return '인증 정보가 유효하지 않습니다. 다시 시도해주세요.';

    // 계정 관리
    case 'auth/account-exists-with-different-credential':
      return '이미 다른 로그인 방식으로 가입된 계정입니다.';

    case 'auth/credential-already-in-use':
      return '이미 다른 계정에서 사용 중인 인증 정보입니다.';

    case 'auth/email-already-exists':
      return '이미 사용 중인 이메일입니다.';

    // 기타
    case 'auth/cancelled-popup-request':
    case 'auth/popup-closed-by-user':
      return '로그인이 취소되었습니다.';

    case 'auth/popup-blocked':
      return '팝업이 차단되었습니다. 팝업 차단을 해제해주세요.';

    case 'auth/internal-error':
      return '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';

    default:
      // 알 수 없는 에러 코드는 원본 메시지 반환 (개발 환경에서 디버깅용)
      console.error('Unhandled Firebase error code:', error.code);
      return error.message || '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
  }
};
